import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { DisplayedDocument, DossierDocumentSign } from './types';
import DossierDocumentSignContext from './DossierDocumentSignContext';
import useDocumentSign from '../hooks/useDocumentSign';
import { useCurrentDossierCtx } from './CurrentDossierProvider';

const DossierDocumentSignProvider: React.FunctionComponent<PropsWithChildren> = function ({
    children,
}) {
    const [dossiers, setDossiers] = useState<Array<DossierDocumentSign>>([]);
    const [raisableDossiers, setRaisableDossiers] = useState<Array<DossierDocumentSign>>([]);
    const [selectedDossier, setSelectedDossier] = useState<DossierDocumentSign>();
    const [loading, setLoading] = useState<boolean>(true);
    const { dossier: currentDossier } = useCurrentDossierCtx();
    const { getDossierSignMetadata, getDossiers, getRaisableDossiers, handleShowDocument } =
        useDocumentSign();

    const loadDossiers = useCallback(async () => {
        if (!currentDossier) {
            return;
        }
        setLoading(true);
        setDossiers([]);
        setRaisableDossiers([]);

        try {
            const metadataResponse = await getDossierSignMetadata(currentDossier.id as number);
            const dossiersData = await getDossiers(metadataResponse);
            setDossiers(dossiersData);
            const raisableDossiersData = await getRaisableDossiers(metadataResponse);
            setRaisableDossiers(raisableDossiersData);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [
        setLoading,
        setDossiers,
        setRaisableDossiers,
        getDossierSignMetadata,
        getRaisableDossiers,
        getDossiers,
        currentDossier,
    ]);

    const getDossiersBySignedFileId = useCallback(
        (fichierId: number): Array<DossierDocumentSign> =>
            dossiers.filter(
                (dossier) =>
                    dossier.documents.find(
                        (document) => document.fileId === fichierId && document.signed,
                    ) !== undefined,
            ),
        [dossiers],
    );

    const isRaisable = useCallback(
        (id: number): boolean => raisableDossiers.find((item) => item.id === id) !== undefined,
        [raisableDossiers],
    );

    const getSignDocItems = useCallback(
        ({
            referenceFichierIds,
            excludeFichierIds,
            onlySigned = false,
        }: {
            referenceFichierIds: Set<number>;
            excludeFichierIds?: Set<number>;
            onlySigned?: boolean;
        }): DisplayedDocument[] => {
            const processedIds = new Set<number>(excludeFichierIds);

            // Return a look alike DisplayedDocument
            return dossiers.flatMap((signDossier) =>
                signDossier.documents
                    .filter((doc) => {
                        if (onlySigned && (!doc.signed || doc.signedFileId == null)) return false;
                        if (!referenceFichierIds.has(doc.fileId)) return false;
                        if (processedIds.has(doc.fileId)) return false;
                        processedIds.add(doc.fileId);
                        return true;
                    })
                    .map((doc) => ({
                        id: doc.fileId,
                        dossierId: currentDossier?.id as number,
                        fichierId: doc.fileId,
                        label: `${doc.name} - Signé`,
                        fileName: doc.fileName,
                        status: (doc.signed ? 'success' : 'pending') as 'success' | 'pending',
                        feedback: (doc.signed ? 'Validé' : 'En attente') as 'Validé' | 'En attente',
                        uploadable: false,
                        uploading: false,
                        editable: false,
                        showable: doc.signed && doc.signedFileId != null,
                        signable: false,
                        canDownloadSigned: doc.signed && doc.signedFileId != null,
                        downloadable: false,
                        removable: false,
                        removableDoc: false,
                        movable: false,
                        hasType: true,
                        type: 'pdf',
                        showStatus: false,

                        // Defined on showDocument at the init ans erase the method passed as props in RequireDocumentList
                        itemActions:
                            doc.signed && doc.signedFileId != null
                                ? {
                                      onShow: () =>
                                          handleShowDocument(
                                              doc.fileName,
                                              doc.signedFileId as number,
                                          ),
                                  }
                                : undefined,
                    })),
            );
        },
        [dossiers, currentDossier, handleShowDocument],
    );

    useEffect(() => {
        if (currentDossier) {
            loadDossiers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentDossier]);

    const contextValue = useMemo(
        () => ({
            dossiers,
            selectedDossier,
            loading,
            loadDossiers,
            setSelectedDossier,
            isRaisable,
            getDossiersBySignedFileId,
            getSignDocItems,
        }),
        [
            dossiers,
            selectedDossier,
            loading,
            loadDossiers,
            isRaisable,
            getDossiersBySignedFileId,
            getSignDocItems,
        ],
    );

    return (
        <DossierDocumentSignContext.Provider value={contextValue}>
            {children}
        </DossierDocumentSignContext.Provider>
    );
};

export default DossierDocumentSignProvider;
